import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { SPRING_SNAP } from '@/lib/motion';
import type { CardLink } from '@/types/database';

interface MediaLightboxProps {
  items: CardLink[];
  /** Current index, or null to close. */
  index: number | null;
  onClose: () => void;
  onNavigate: (next: number) => void;
}

export function MediaLightbox({ items, index, onClose, onNavigate }: MediaLightboxProps) {
  const open = index !== null;
  const item = open ? items[index!] : null;

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && index! < items.length - 1) onNavigate(index! + 1);
      else if (e.key === 'ArrowLeft' && index! > 0) onNavigate(index! - 1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, index, items.length, onClose, onNavigate]);

  return (
    <AnimatePresence>
      {open && item && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center safe-pt safe-pb"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.button
            type="button"
            aria-label="Close"
            className="absolute inset-0 bg-ink/95 backdrop-blur-xl"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute right-5 top-5 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-surface/10 text-bone backdrop-blur-md transition-colors hover:bg-surface/20 safe-pt"
          >
            <X className="h-5 w-5" />
          </button>

          {index! > 0 && (
            <button
              onClick={() => onNavigate(index! - 1)}
              aria-label="Previous"
              className="absolute left-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-surface/10 text-bone backdrop-blur-md transition-colors hover:bg-surface/20"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          {index! < items.length - 1 && (
            <button
              onClick={() => onNavigate(index! + 1)}
              aria-label="Next"
              className="absolute right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-surface/10 text-bone backdrop-blur-md transition-colors hover:bg-surface/20"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}

          <motion.div
            key={item.id}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={SPRING_SNAP}
            className="relative flex max-h-[90vh] max-w-[92vw] flex-col items-center gap-3"
          >
            {item.value ? (
              <img
                src={item.value}
                alt={item.description ?? ''}
                className="max-h-[80vh] max-w-[92vw] rounded-lg object-contain"
              />
            ) : (
              <div className="rounded-lg bg-bone/10 px-12 py-24 text-bone/60">No image</div>
            )}
            {item.description && (
              <p className="max-w-md text-balance text-center text-sm text-bone/80">
                {item.description}
              </p>
            )}
            <p className="font-mono text-[10px] uppercase tracking-widest text-bone/40">
              {(index ?? 0) + 1} / {items.length}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
